import { useState } from 'react';
import { getUserProfile } from '../../utils/storage';
import { useAuth } from '../../contexts/AuthContext';

const FORM_TYPES = [
  {
    id: 'issue',
    emoji: '🐛',
    label: 'Report an Issue',
    desc: 'Something broken or not working right',
  },
  {
    id: 'feature',
    emoji: '💡',
    label: 'Feature Request',
    desc: 'Suggest a new feature or improvement',
  },
  {
    id: 'partnership',
    emoji: '🤝',
    label: 'Partnership Inquiry',
    desc: 'Business, dealer, or integration opportunities',
  },
];

const ISSUE_CATEGORIES = [
  'Vehicle analysis',
  'Market valuation',
  'Chat / AI assistant',
  'Auto Advisor',
  'Service history',
  'Account / login',
  'Other',
];

const FEATURE_CATEGORIES = [
  'New vehicle feature',
  'Garage management',
  'AI / analysis',
  'UI / design',
  'Integrations',
  'Other',
];

export default function ContactSupportModal({ onClose, onToast }) {
  const { user } = useAuth();
  const profile = getUserProfile();
  const userEmail = user?.email || profile?.email || '';

  const [step, setStep] = useState('choose'); // 'choose' | 'issue' | 'feature' | 'partnership'

  // Shared form state
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('');

  // Partnership-specific
  const [companyName, setCompanyName] = useState('');
  const [partnerType, setPartnerType] = useState('');

  function resetForm() {
    setSubject('');
    setDescription('');
    setCategory('');
    setPriority('');
    setCompanyName('');
    setPartnerType('');
  }

  function buildMailtoLink() {
    const to = 'support@propermiles.com';
    let subjectLine = '';
    let body = '';

    const userInfo = `\n\n---\nSent from Proper Miles Mobile Garage\nUser: ${profile?.firstName || ''} ${profile?.lastName || ''}\nEmail: ${userEmail}\nPlan: ${profile?.subscription?.plan || 'Free'}`;

    if (step === 'issue') {
      subjectLine = `[Bug Report] ${category ? `[${category}] ` : ''}${subject}`;
      body = `Category: ${category}\nPriority: ${priority}\n\n${description}${userInfo}`;
    } else if (step === 'feature') {
      subjectLine = `[Feature Request] ${category ? `[${category}] ` : ''}${subject}`;
      body = `Category: ${category}\n\n${description}${userInfo}`;
    } else if (step === 'partnership') {
      subjectLine = `[Partnership] ${companyName ? `${companyName} - ` : ''}${subject}`;
      body = `Company: ${companyName}\nPartnership Type: ${partnerType}\n\n${description}${userInfo}`;
    }

    return `mailto:${to}?subject=${encodeURIComponent(subjectLine)}&body=${encodeURIComponent(body)}`;
  }

  function handleSubmit() {
    if (!subject.trim() || !description.trim()) {
      onToast?.('Please fill in subject and description');
      return;
    }
    window.location.href = buildMailtoLink();
    onToast?.('Opening email client...');
    onClose();
  }

  // ── Choose step ──
  if (step === 'choose') {
    return (
      <Overlay onClose={onClose}>
        <h3 style={titleStyle}>Contact Support</h3>
        <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 20, textAlign: 'center' }}>
          How can we help?
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {FORM_TYPES.map((type) => (
            <button
              key={type.id}
              onClick={() => { resetForm(); setStep(type.id); }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '14px 16px',
                borderRadius: 'var(--radius-sm)',
                border: '1.5px solid var(--border)',
                background: 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>{type.emoji}</span>
              <div>
                <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-primary)' }}>{type.label}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{type.desc}</div>
              </div>
            </button>
          ))}
        </div>

        <button onClick={onClose} style={{ ...btnSecondary, marginTop: 16 }}>
          Cancel
        </button>
      </Overlay>
    );
  }

  // ── Issue Report ──
  if (step === 'issue') {
    const canSubmit = subject.trim() && description.trim() && category && priority;
    return (
      <Overlay onClose={onClose}>
        <h3 style={titleStyle}>Report an Issue</h3>

        <div className="form-group">
          <label className="form-label">Category</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {ISSUE_CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                style={chipStyle(category === c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Priority</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {['Low', 'Medium', 'High'].map((p) => (
              <button
                key={p}
                onClick={() => setPriority(p)}
                style={{
                  ...chipStyle(priority === p),
                  ...(priority === p && p === 'High' ? { borderColor: 'var(--danger)', background: 'rgba(239,68,68,0.1)', color: 'var(--danger)' } : {}),
                }}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Subject</label>
          <input
            className="form-input"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Brief summary of the issue"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea
            className="form-input"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What happened? What did you expect?"
            style={{ resize: 'vertical', minHeight: 80, fontFamily: 'inherit', lineHeight: 1.5 }}
          />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setStep('choose')} style={btnSecondary}>Back</button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              ...btnPrimary,
              opacity: canSubmit ? 1 : 0.5,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            Send Report
          </button>
        </div>
      </Overlay>
    );
  }

  // ── Feature Request ──
  if (step === 'feature') {
    const canSubmit = subject.trim() && description.trim() && category;
    return (
      <Overlay onClose={onClose}>
        <h3 style={titleStyle}>Feature Request</h3>

        <div className="form-group">
          <label className="form-label">Category</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {FEATURE_CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                style={chipStyle(category === c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Subject</label>
          <input
            className="form-input"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="What feature would you like?"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea
            className="form-input"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the feature and why it would be useful..."
            style={{ resize: 'vertical', minHeight: 80, fontFamily: 'inherit', lineHeight: 1.5 }}
          />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setStep('choose')} style={btnSecondary}>Back</button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              ...btnPrimary,
              opacity: canSubmit ? 1 : 0.5,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            Send Request
          </button>
        </div>
      </Overlay>
    );
  }

  // ── Partnership Inquiry ──
  if (step === 'partnership') {
    const PARTNER_TYPES = ['Dealership', 'Shop / Mechanic', 'Parts Supplier', 'Technology', 'Media / Content', 'Other'];
    const canSubmit = subject.trim() && description.trim() && partnerType;
    return (
      <Overlay onClose={onClose}>
        <h3 style={titleStyle}>Partnership Inquiry</h3>

        <div className="form-group">
          <label className="form-label">Partnership Type</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {PARTNER_TYPES.map((t) => (
              <button
                key={t}
                onClick={() => setPartnerType(t)}
                style={chipStyle(partnerType === t)}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Company Name (optional)</label>
          <input
            className="form-input"
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Your company or business name"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Subject</label>
          <input
            className="form-input"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Brief summary of your proposal"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea
            className="form-input"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Tell us about your partnership idea..."
            style={{ resize: 'vertical', minHeight: 80, fontFamily: 'inherit', lineHeight: 1.5 }}
          />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setStep('choose')} style={btnSecondary}>Back</button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              ...btnPrimary,
              opacity: canSubmit ? 1 : 0.5,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            Send Inquiry
          </button>
        </div>
      </Overlay>
    );
  }

  return null;
}

/* ─── Shared sub-components ─── */

function Overlay({ children, onClose }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 200, padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-card)',
          borderRadius: 'var(--radius-md)',
          padding: 24, width: '100%', maxWidth: 420,
          border: '1px solid var(--border)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        {children}
      </div>
    </div>
  );
}

/* ─── Shared styles ─── */

const titleStyle = {
  fontSize: '1.125rem',
  fontWeight: 700,
  color: 'var(--text-primary)',
  textAlign: 'center',
  marginBottom: 8,
};

function chipStyle(selected) {
  return {
    padding: '8px 14px',
    borderRadius: 100,
    fontSize: '0.8125rem',
    fontWeight: 600,
    border: `1.5px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
    background: selected ? 'var(--accent-subtle)' : 'transparent',
    color: selected ? 'var(--accent)' : 'var(--text-secondary)',
    cursor: 'pointer',
  };
}

const btnSecondary = {
  flex: 1,
  padding: '12px 16px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'transparent',
  color: 'var(--text-secondary)',
  fontWeight: 600,
  fontSize: '0.875rem',
  cursor: 'pointer',
};

const btnPrimary = {
  flex: 1,
  padding: '12px 16px',
  borderRadius: 'var(--radius-sm)',
  border: 'none',
  background: 'var(--accent)',
  color: 'white',
  fontWeight: 600,
  fontSize: '0.875rem',
  cursor: 'pointer',
};
